/**
 * Runtime side of the baked cockpit compositions: fetch one aircraft's
 * `data/baked/cockpits/<KEY>.json`, its palette, and the sprite sheets it
 * draws from.
 *
 * The composition itself is *decoded data*, not code — produced by
 * `tools/bake`'s `cockpits` stage from the aircraft's own `.TTM` (see
 * `packages/baker/src/cockpit-bake.ts` for the disassembly account). Nothing
 * about the layout is authored here; this module only resolves which file to
 * fetch and turns the palette into CSS colours.
 */

import type { CockpitComposition, CockpitIndex } from "@red-opus/baker/schema";

export type { CockpitComposition, CockpitDrawOp, CockpitGaugeRole, CockpitGaugeSlot } from "@red-opus/baker/schema";

const BAKED = "/baked/";

/**
 * The composition every aircraft without a cockpit script of its own borrows.
 * All 22 flyable scouts have their own `.TTM`, so this only ever applies to
 * the AI-only two-seaters and bombers (`BE2`, `AVIATIK`, `FE2`, `ROLAND`,
 * `RE8`, `RUMPLER`, `BRISTOL`, `DH4`, `JUNKER`, `GOTHA`, `HANDLEY`), which
 * the player never flies from the cockpit — a fallback for robustness, not
 * an approximation anyone is expected to see.
 */
export const FALLBACK_AIRCRAFT = "CAMEL";

export function cockpitCompositionPath(aircraft: string): string {
  return `${BAKED}cockpits/${aircraft.toUpperCase()}.json`;
}

export function spriteSheetPath(sheet: string): string {
  return `${BAKED}sprites/${sheet}.png`;
}

export function palettePath(palette: string): string {
  return `${BAKED}palettes/${palette}.json`;
}

/** Which composition to fetch for `aircraft`, given what the bake produced. */
export function resolveCockpitAircraft(index: CockpitIndex | undefined, aircraft: string | undefined): string {
  const wanted = (aircraft ?? "").toUpperCase();
  if (wanted && index?.aircraft.includes(wanted)) return wanted;
  const mapped = wanted ? index?.fallbacks[wanted] : undefined;
  return mapped ?? FALLBACK_AIRCRAFT;
}

/**
 * Just the muzzle points, without the palette or the sheet images — what
 * `flight-scene.ts` needs to make the player's rounds leave the guns rather
 * than the fuselage centreline. Resolves through the same fallback as
 * `loadCockpit`, and returns `[]` if the bake has no cockpits (in which case
 * bullets simply keep the sim's own origin).
 */
export async function loadCockpitMuzzles(aircraft: string | undefined): Promise<readonly { x: number; y: number }[]> {
  const index = await fetchJson<CockpitIndex>(`${BAKED}cockpits/index.json`);
  const composition = await fetchJson<CockpitComposition>(cockpitCompositionPath(resolveCockpitAircraft(index, aircraft)));
  return composition.muzzles;
}

/** `[r,g,b]` triples straight out of a baked `.PAL.json`. */
export interface BakedPalette {
  readonly entries: number;
  readonly colors: readonly (readonly number[])[];
}

/** Palette index -> `"#rrggbb"`, pre-resolved once per palette so the draw loop never allocates a colour string. */
export function paletteCssColors(palette: BakedPalette): string[] {
  return palette.colors.map((rgb) => {
    const [r = 0, g = 0, b = 0] = rgb;
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  });
}

/**
 * One fetch per URL per document.
 *
 * The reason is measured rather than tidy-minded: `/fly` calls
 * `loadCockpitMuzzles` (from the scene's boot path) and `loadCockpit` (from the
 * cockpit panel) at almost the same moment, and each of them fetched
 * `cockpits/index.json` **and** the composition. A Camel's composition is
 * **445 KB**, so every flight pulled 890 KB over the wire for 445 KB of data
 * and the two requests then shared the link, so *both* took twice as long as
 * one would have — 390 ms apiece on a 20 Mbit connection
 * (`docs/engineering/fly-boot-timing.md` §3). `Cache-Control: no-store` on the
 * dev server means the HTTP cache cannot dedupe this, and the service worker
 * only helps on the second flight, so it has to be deduped here.
 *
 * The *promise* is cached, not the body, which is what makes it a dedupe rather
 * than only a cache: the second caller joins the request already in flight. A
 * rejection is evicted so the next call retries instead of replaying the same
 * failure forever — the same rule `world/region-loader.ts` states for regions.
 */
const jsonCache = new Map<string, Promise<unknown>>();

async function fetchJson<T>(url: string): Promise<T> {
  const cached = jsonCache.get(url);
  if (cached) return cached as Promise<T>;
  const pending = fetch(url).then(async (response) => {
    if (!response.ok) throw new Error(`cockpit: ${url} -> HTTP ${response.status}`);
    return (await response.json()) as T;
  });
  pending.catch(() => jsonCache.delete(url));
  jsonCache.set(url, pending);
  return pending;
}

export interface LoadedCockpit {
  readonly composition: CockpitComposition;
  readonly colors: readonly string[];
  /** Loaded sheet images, keyed by the sheet name the composition's ops use. */
  readonly sheets: ReadonlyMap<string, HTMLImageElement>;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`cockpit: failed to load ${src}`));
    image.src = src;
  });
}

/**
 * Fetches everything one cockpit needs. `aircraft` is the engine's
 * `AircraftKey` (`"CAMEL"`); an unknown or missing one resolves through
 * `resolveCockpitAircraft` rather than failing.
 */
export async function loadCockpit(aircraft: string | undefined): Promise<LoadedCockpit> {
  const index = await fetchJson<CockpitIndex>(`${BAKED}cockpits/index.json`);
  const key = resolveCockpitAircraft(index, aircraft);
  const composition = await fetchJson<CockpitComposition>(cockpitCompositionPath(key));
  const palette = await fetchJson<BakedPalette>(palettePath(composition.palette));
  const names = Object.keys(composition.sheetFrames);
  const images = await Promise.all(names.map((name) => loadImage(spriteSheetPath(name))));
  return {
    composition,
    colors: paletteCssColors(palette),
    sheets: new Map(names.map((name, i) => [name, images[i]!])),
  };
}
